/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useShellScreenHeaderRefresh } from '../../shell/navigation/TabScreenRefreshContext';
import { useResetModuleUiOnBlur } from '../../shell/navigation/useResetModuleUiOnBlur';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { spacing } from '../../shared/theme/spacing';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { SalesPeriodChip } from '../components/SalesPeriodChip';
import { SalesScreenHeader } from '../components/SalesScreenHeader';
import { SalesVolumeSoftLimitRow } from '../components/SalesVolumeSoftLimitRow';
import { SalesFilterPanel } from '../components/SalesFilterPanel';
import { SalesTable } from '../components/SalesTable';
import { SaleListRowCard } from '../components/SaleListRowCard';
import { SaleDetailModal } from '../components/SaleDetailModal';
import { SalesExportConfirmModal } from '../components/SalesExportConfirmModal';
import { buildSalesExportScopeSummary } from '../application/salesExportScope';
import { useSalesListScreen } from '../application/useSalesListScreen'
import type { SalesListRouteParams } from '../application/salesNavigation'
import { getSalesDateRangeLabel } from '../infra/salesDateRange'
import { DATE_RANGE_PRESET_LABELS, POS_SALES_HISTORY_DATE_PRESETS, type DateRangePreset } from '../infra/salesReportingConstants';
import { buildPaymentMethodNames, DEFAULT_PAYMENT_METHOD_LABELS } from '../infra/salesPaymentLabels';
import { getPaymentMethods, type PaymentMethodRow } from '../infra/salesRepo';
import type { SaleListRow } from '../domain/salesTypes';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../team/public-api';
import { buildAuthUserLabelById, resolveAuthUserDisplayLabel } from '../../auth/domain/authUserDisplayLabels';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { fetchHospitalitySnapshot } from '../../hospitality/infra/hospitalityRepo';

type SalesListRoute = RouteProp<{ SalesList: SalesListRouteParams }, 'SalesList'>;

const LIST_WEB_EXTRA_BOTTOM = 16;

/** Presets del picker de período — misma fuente que los chips del panel de filtros. */
const PERIOD_SHEET_OPTIONS: { id: string; name: string }[] = [
  ...POS_SALES_HISTORY_DATE_PRESETS.map((id) => ({ id, name: DATE_RANGE_PRESET_LABELS[id] })),
  { id: 'custom', name: 'Otras fechas…' },
];

/** Label corto del chip de período: preset o `dd/mm – dd/mm` (año solo si ≠ actual). */
function periodChipLabel(preset: DateRangePreset | null, fromYmd: string, toYmd: string): string {
  if (preset != null && preset !== 'custom') return DATE_RANGE_PRESET_LABELS[preset];
  const currentYear = String(new Date().getFullYear());
  const fmt = (ymd: string) => {
    const [y, m, d] = ymd.split('-');
    if (!y || !m || !d) return ymd;
    return y === currentYear ? `${d}/${m}` : `${d}/${m}/${y}`;
  };
  return fromYmd === toYmd ? fmt(fromYmd) : `${fmt(fromYmd)} – ${fmt(toYmd)}`;
}

export function SalesListScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const route = useRoute<SalesListRoute>();
  const navigation = useNavigation<any>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const insets = useSafeAreaInsets();
  const { layoutWide } = useContentLayout('dataWide');

  const initialParams = useMemo(() => route.params ?? {}, [route.params]);
  const vm = useSalesListScreen({ branchId, initialParams });
  const showChannelFilter =
    usePublicStoreFeaturesVisible() || vm.hasOnlineSalesHistory === true;
  // "Pedidos cerrados" en el Más — mismo gate que la pantalla OrdersHistory.
  const canOpenOrdersHistory = useHasBusinessCapability('delivery_fulfillment');

  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const ownerAuthUserId = sessionUser?.id ?? null;

  const cashierDisplayName = useMemo(
    () => {
      const labelById = buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId,
        employees,
      });
      return (userId: string | null) =>
        resolveAuthUserDisplayLabel(userId, labelById, ownerAuthUserId);
    },
    [sessionUser, ownerAuthUserId, employees]
  );

  useShellScreenHeaderRefresh('SalesList', vm.onRefresh);

  const [detailSaleId, setDetailSaleId] = useState<string | null>(route.params?.saleId ?? null);
  const modalBackdropPadding = useModalBackdropPadding();
  const [customDatesOpen, setCustomDatesOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);

  useResetModuleUiOnBlur(
    useCallback(() => {
      setDetailSaleId(null);
      setFilterSheetOpen(false);
      setCustomDatesOpen(false);
      setPeriodSheetOpen(false);
      setMoreSheetOpen(false);
      setExportConfirmOpen(false);
    }, [])
  );

  const [diningTableNames, setDiningTableNames] = useState<Record<string, string>>({});
  const hasTables = useHasBusinessCapability('tables');

  useEffect(() => {
    let cancelled = false;
    if (!branchId || !hasTables) {
      setDiningTableNames({});
      return;
    }
    fetchHospitalitySnapshot(branchId)
      .then((snapshot) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const table of snapshot.diningTables) {
          map[table.id] = table.name;
        }
        setDiningTableNames(map);
      })
      .catch(() => {
        if (cancelled) return;
        setDiningTableNames({});
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, hasTables]);

  const openDetail = useCallback((row: SaleListRow) => {
    setDetailSaleId(row.id);
  }, []);

  const dateRangeLabel = useMemo(() => {
    const preset = vm.activeDatePreset;
    if (preset == null || preset === 'custom') {
      const from = vm.appliedFromYmd || vm.draftFromYmd;
      const to = vm.appliedToYmd || vm.draftToYmd;
      return from === to ? from : `${from} — ${to}`;
    }
    return getSalesDateRangeLabel(preset);
  }, [vm.activeDatePreset, vm.appliedFromYmd, vm.appliedToYmd, vm.draftFromYmd, vm.draftToYmd]);

  const [paymentMethodOptions, setPaymentMethodOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [paymentMethodCatalogNames, setPaymentMethodCatalogNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    void getPaymentMethods()
      .then((methods: PaymentMethodRow[]) => {
        if (!alive) return;
        // Bug fix: métodos custom (id no-default) mostraban el id crudo en las
        // filas porque paymentMethodNames solo conocía DEFAULT_* + ids en pantalla.
        setPaymentMethodCatalogNames(buildPaymentMethodNames(methods));
        const defaults = Object.entries(DEFAULT_PAYMENT_METHOD_LABELS).map(([id, name]) => ({ id, name }));
        const fromDb = methods
          .filter((m) => !DEFAULT_PAYMENT_METHOD_LABELS[m.id])
          .map((m) => ({ id: m.id, name: m.name }));
        const merged = [...defaults, ...fromDb].sort((a, b) => a.name.localeCompare(b.name));
        setPaymentMethodOptions(merged);
      })
      .catch(() => {
        if (!alive) return;
        setPaymentMethodCatalogNames({});
        const defaults = Object.entries(DEFAULT_PAYMENT_METHOD_LABELS).map(([id, name]) => ({ id, name }));
        setPaymentMethodOptions(defaults);
      });
    return () => { alive = false; };
  }, []);

  const paymentMethodNames = useMemo(
    () => ({ ...DEFAULT_PAYMENT_METHOD_LABELS, ...paymentMethodCatalogNames }),
    [paymentMethodCatalogNames]
  );

  // Props compartidas para el sheet de filtros (proto: todo detrás del
  // botón "Filtros" — no hay toolbar inline en el listado).
  const filterPanelProps = useMemo(
    () => ({
      activeDatePreset: vm.activeDatePreset,
      onPreset: vm.onPreset,
      onOpenCustomDates: () => setCustomDatesOpen(true),
      dateRangeLabel,
      cashierFilter: vm.cashierFilter,
      onCashierFilterChange: vm.onCashierFilterChange,
      cashierOptions: vm.cashierOptions,
      primaryStaffFilter: vm.primaryStaffFilter,
      onPrimaryStaffFilterChange: vm.onPrimaryStaffFilterChange,
      primaryStaffOptions: vm.primaryStaffOptions,
      statusFilter: vm.statusFilter,
      onStatusFilterChange: vm.onStatusFilterChange,
      paymentMethodFilter: vm.paymentMethodFilter,
      onPaymentMethodFilterChange: vm.onPaymentMethodFilterChange,
      paymentMethodOptions,
      paymentPendingOnly: vm.paymentPendingOnly,
      onPaymentPendingOnlyChange: vm.onPaymentPendingOnlyChange,
      fiscalStatusFilter: vm.fiscalStatusFilter,
      onFiscalStatusFilterChange: vm.onFiscalStatusFilterChange,
      channelFilter: vm.channelFilter,
      onChannelFilterChange: vm.onChannelFilterChange,
      showChannelFilter,
      commissionFilter: vm.commissionFilter,
      onCommissionFilterChange: vm.onCommissionFilterChange,
      showCommissionFilter: vm.canViewCommissions,
      hasActiveFilters: vm.hasActiveListFilters,
      onClearFilters: vm.clearListFilters,
      sortKey: vm.sortKey,
      sortDir: vm.sortDir,
      onSortChange: vm.onSortChange,
    }),
    [
      vm.activeDatePreset,
      vm.onPreset,
      dateRangeLabel,
      vm.cashierFilter,
      vm.onCashierFilterChange,
      vm.cashierOptions,
      vm.primaryStaffFilter,
      vm.onPrimaryStaffFilterChange,
      vm.primaryStaffOptions,
      vm.statusFilter,
      vm.onStatusFilterChange,
      vm.paymentMethodFilter,
      vm.onPaymentMethodFilterChange,
      paymentMethodOptions,
      vm.paymentPendingOnly,
      vm.onPaymentPendingOnlyChange,
      vm.fiscalStatusFilter,
      vm.onFiscalStatusFilterChange,
      vm.channelFilter,
      vm.onChannelFilterChange,
      showChannelFilter,
      vm.commissionFilter,
      vm.onCommissionFilterChange,
      vm.canViewCommissions,
      vm.hasActiveListFilters,
      vm.clearListFilters,
      vm.sortKey,
      vm.sortDir,
      vm.onSortChange,
    ]
  );

  const filterSheetContent = useMemo(
    () => <SalesFilterPanel {...filterPanelProps} sheetMode />,
    [filterPanelProps]
  );

  const periodTrigger = useMemo(
    () => (
      <SalesPeriodChip
        label={periodChipLabel(vm.activeDatePreset, vm.appliedFromYmd, vm.appliedToYmd)}
        onPress={() => setPeriodSheetOpen(true)}
        variant={layoutWide ? 'desk' : 'ticket'}
        testID="ventas.period-chip"
      />
    ),
    [vm.activeDatePreset, vm.appliedFromYmd, vm.appliedToYmd, layoutWide]
  );

  const filterTrigger = useMemo(
    // Desk: el botón Filtros convive con la toolbar inline (abre el sheet
    // completo con sort + fechas custom). Mobile: full-width en el header.
    () => (
      <FilterSheetButton
        activeCount={vm.activeListFilterCount}
        onOpen={() => setFilterSheetOpen(true)}
        accessibilityLabel="Abrir filtros de ventas"
        variant={layoutWide ? 'desk' : 'ticket'}
        style={layoutWide ? undefined : styles.filterTriggerMobile}
        testID="ventas.filters"
      />
    ),
    [vm.activeListFilterCount, layoutWide]
  );

  const moreTrigger = useMemo(
    () =>
      layoutWide ? (
        <TouchableOpacity
          style={styles.moreTileDesk}
          onPress={() => setMoreSheetOpen(true)}
          accessibilityRole="button"
          accessibilityLabel="Más acciones"
          testID="ventas.more"
        >
          <Text style={styles.moreTileDeskText}>Más</Text>
          <Ionicons name="chevron-down" size={14} color={ticketColors.ink} />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={styles.moreTileMobile}
          onPress={() => setMoreSheetOpen(true)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Más acciones"
          testID="ventas.more"
        >
          <Ionicons name="ellipsis-horizontal" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      ),
    [layoutWide]
  );

  const listHeader = useMemo(
    () => (
      <>
        <SalesScreenHeader
          rows={vm.rows}
          loading={vm.loading}
          search={vm.search}
          onSearchChange={vm.setSearch}
          onExportCsv={() => setExportConfirmOpen(true)}
          canExport={vm.canExport}
          serverKpis={vm.serverKpis}
          filterTrigger={filterTrigger}
          periodTrigger={periodTrigger}
          moreTrigger={moreTrigger}
          pendingKpiSelected={vm.paymentPendingOnly}
          onPendingKpiPress={() => vm.onPaymentPendingOnlyChange(!vm.paymentPendingOnly)}
          voidedKpiSelected={vm.statusFilter === 'voided'}
          onVoidedKpiPress={() =>
            vm.onStatusFilterChange(vm.statusFilter === 'voided' ? 'active' : 'voided')
          }
        />
      </>
    ),
    [vm, filterTrigger, periodTrigger, moreTrigger, layoutWide, filterPanelProps]
  );

  const exportScopeSummary = useMemo(() => {
    const cashierLabel =
      vm.cashierFilter !== 'all'
        ? vm.cashierOptions.find((o) => o.key === vm.cashierFilter)?.label ?? null
        : null;
    const primaryStaffLabel =
      vm.primaryStaffFilter !== 'all'
        ? vm.primaryStaffOptions.find((o) => o.key === vm.primaryStaffFilter)?.label ?? null
        : null;
    const paymentMethodLabel =
      vm.paymentMethodFilter !== 'all'
        ? paymentMethodOptions.find((o) => o.id === vm.paymentMethodFilter)?.name ?? null
        : null;
    return buildSalesExportScopeSummary({
      dateRangeLabel,
      search: vm.search,
      statusFilter: vm.statusFilter,
      cashierLabel,
      primaryStaffLabel,
      paymentMethodLabel,
      fiscalStatusFilter: vm.fiscalStatusFilter,
      channelFilter: vm.channelFilter,
      paymentPendingOnly: vm.paymentPendingOnly,
      commissionFilter: vm.canViewCommissions ? vm.commissionFilter : 'all',
      sortKey: vm.sortKey,
      sortDir: vm.sortDir,
    });
  }, [
    dateRangeLabel,
    vm.search,
    vm.statusFilter,
    vm.cashierFilter,
    vm.cashierOptions,
    vm.primaryStaffFilter,
    vm.primaryStaffOptions,
    vm.paymentMethodFilter,
    paymentMethodOptions,
    vm.fiscalStatusFilter,
    vm.channelFilter,
    vm.paymentPendingOnly,
    vm.commissionFilter,
    vm.canViewCommissions,
    vm.sortKey,
    vm.sortDir,
  ]);

  const moreActions = useMemo(
    () => [
      ...(vm.canExport
        ? [{
            key: 'export',
            label: 'Exportar CSV',
            hint: vm.totalCount != null ? `${vm.totalCount} ventas · todas las páginas` : 'Todas las páginas',
            icon: 'download-outline' as const,
            onPress: () => {
              setMoreSheetOpen(false);
              setExportConfirmOpen(true);
            },
            testID: 'ventas.more.export',
          }]
        : []),
      {
        key: 'period',
        label: 'Cambiar período',
        hint: dateRangeLabel,
        icon: 'calendar-outline' as const,
        onPress: () => {
          setMoreSheetOpen(false);
          setPeriodSheetOpen(true);
        },
        testID: 'ventas.more.period',
      },
      ...(canOpenOrdersHistory
        ? [{
            key: 'ordersHistory',
            label: 'Pedidos cerrados',
            hint: 'Entregados y cancelados',
            icon: 'bicycle-outline' as const,
            onPress: () => {
              setMoreSheetOpen(false);
              navigation.navigate('OrdersHistory');
            },
            testID: 'ventas.more.orders-history',
          }]
        : []),
    ],
    [vm.canExport, vm.totalCount, dateRangeLabel, canOpenOrdersHistory, navigation]
  );

  const pagination = (
    <TicketDeskFooter
      pageIndex={vm.pageIndex}
      pageSize={vm.pageSize}
      productCount={vm.rows.length}
      totalCount={vm.totalCount}
      hasMore={vm.hasMore}
      loading={vm.loading}
      onPreviousPage={vm.goToPreviousPage}
      onNextPage={vm.goToNextPage}
      onGoToPage={vm.goToPage}
      embedded
    />
  );

  const emptyList = (
    <ModuleEmptyState
      variant="nest"
      title={
        vm.loadError
          ? 'No se pudieron cargar las ventas'
          : vm.paymentPendingOnly
            ? 'Sin ventas pendientes de cobro'
            : vm.channelFilter === 'online'
              ? 'Sin ventas de pedido online'
              : vm.channelFilter === 'pos'
                ? 'Sin ventas de mostrador'
                : 'Sin ventas en el período'
      }
      subtitle={
        vm.loadError
          ? 'Revisá tu conexión y reintentá.'
          : vm.search.trim().length > 0
            ? 'No hay ventas con esa búsqueda.'
            : vm.paymentPendingOnly
              ? 'Las ventas a cuenta o con pago parcial aparecen acá.'
              : vm.channelFilter === 'online'
                ? 'Cuando el pedido se completa, aparece acá. Lo pendiente está en Pedidos.'
                : vm.channelFilter === 'pos'
                  ? 'Acá están las cobradas en el mostrador.'
                  : showChannelFilter
                    ? 'Mostrador y pedidos ya entregados. Lo que está en curso está en Pedidos.'
                    : 'No hay ventas con esos filtros.'
      }
      secondaryAction={
        vm.loadError
          ? { label: 'Reintentar', onPress: () => void vm.reloadList() }
          : vm.hasActiveListFilters ? { label: 'Limpiar filtros', onPress: vm.clearListFilters } : undefined
      }
      testID="ventas.empty"
    />
  );

  const salesRows = useMemo(
    () => vm.rows.map((item) => (
      <SaleListRowCard
        key={item.id}
        row={item}
        onPress={() => openDetail(item)}
        paymentMethodNames={paymentMethodNames}
        diningTableName={item.diningTableId ? diningTableNames[item.diningTableId] : null}
      />
    )),
    [vm.rows, openDetail, paymentMethodNames, diningTableNames]
  );

  const listContentStyle = useMemo(
    () => ({
      paddingBottom: insets.bottom + LIST_WEB_EXTRA_BOTTOM,
      flexGrow: vm.rows.length === 0 ? 1 : undefined,
    }),
    [insets.bottom, vm.rows.length]
  );

  const fullEmptyState = (
    <ModuleEmptyState
      variant="nest"
      kicker="Ventas"
      title="Elegí una sucursal"
      subtitle="Elegila en el encabezado para ver las ventas."
    />
  );

  // Skeleton de carga (proto ventas-ux-v1 §5): filas placeholder que comunican
  // el layout de la lista en vez de un spinner solo.
  const skeletonRows = (
    <View testID="ventas.skeleton" accessibilityLabel="Cargando ventas">
      {[0, 1, 2, 3, 4].map((i) => (
        <View key={i} style={styles.skeletonRow}>
          <View style={[styles.skeletonBlock, { width: 52 }]} />
          <View style={styles.skeletonTextCol}>
            <View style={[styles.skeletonBlock, { width: '58%' }]} />
            <View style={[styles.skeletonBlock, styles.skeletonBlockThin, { width: '78%' }]} />
          </View>
          <View style={[styles.skeletonBlock, { width: 64 }]} />
        </View>
      ))}
    </View>
  );

  const listPlaceholder = vm.loading && vm.rows.length === 0 ? skeletonRows : emptyList;

  return (
    <OperationalScrollScreen
      header={listHeader}
      body={
        <PaperSheet style={{ minHeight: 0 }} testID="ventas.list.sheet">
          <SalesVolumeSoftLimitRow />
          {layoutWide ? (
            vm.rows.length === 0 ? listPlaceholder : (
              <SalesTable
                rows={vm.rows}
                onOpenDetail={openDetail}
                paymentMethodNames={paymentMethodNames}
                diningTableNames={diningTableNames}
                cashierDisplayName={cashierDisplayName}
                selfUserId={sessionUser?.id ?? null}
                sortKey={vm.sortKey}
                sortDir={vm.sortDir}
                onSortChange={vm.onSortChange}
                embedded
              />
            )
          ) : (
            vm.rows.length === 0 ? listPlaceholder : salesRows
          )}
          {vm.rows.length > 0 ? pagination : null}
        </PaperSheet>
      }
      fullEmptyState={!branchId ? fullEmptyState : undefined}
      refreshing={vm.refreshing}
      onRefresh={vm.onRefresh}
      contentContainerStyle={listContentStyle}
      containerStyle={styles.shell}
      containerCompactStyle={styles.shellCompact}
      layoutWide={layoutWide}
      testID="ventas.screen"
      modals={
        <>
          <SaleDetailModal
            visible={detailSaleId != null}
            saleId={detailSaleId}
            branchId={branchId ?? null}
            onClose={() => setDetailSaleId(null)}
            onDetailMutated={() => {
              void vm.reloadList();
            }}
          />

          <MasterFilterSheetModal
            visible={periodSheetOpen}
            title="Período"
            options={PERIOD_SHEET_OPTIONS}
            selectedId={vm.activeDatePreset ?? 'custom'}
            showAllOption={false}
            appearance="ticket"
            onSelect={(id) => {
              setPeriodSheetOpen(false);
              if (id === 'custom') {
                setCustomDatesOpen(true);
              } else if (id) {
                vm.onPreset(id as DateRangePreset);
              }
            }}
            onClose={() => setPeriodSheetOpen(false)}
          />

          <FilterSheetModal
            visible={filterSheetOpen}
            onClose={() => setFilterSheetOpen(false)}
            onApply={() => setFilterSheetOpen(false)}
            onClear={() => vm.clearListFilters()}
            hasActiveDraft={vm.hasActiveListFilters}
            title="Filtros"
            applyLabel="Aplicar"
            appearance="ticket"
          >
            {filterSheetContent}
          </FilterSheetModal>

          <TicketMoreActionsSheet
            visible={moreSheetOpen}
            title="Más acciones"
            actions={moreActions}
            onClose={() => setMoreSheetOpen(false)}
          />

          <SalesExportConfirmModal
            visible={exportConfirmOpen}
            scopeSummary={exportScopeSummary}
            estimatedCount={vm.totalCount}
            onClose={() => setExportConfirmOpen(false)}
            onConfirm={() => {
              setExportConfirmOpen(false);
              void vm.onExportCsv();
            }}
          />

          {customDatesOpen ? (
            <Modal visible transparent animationType="fade" onRequestClose={() => setCustomDatesOpen(false)} accessibilityViewIsModal>
              <View style={[posUi.modalBackdrop, modalExperience.modalRoot, modalBackdropPadding]}>
                <View
                  style={[
                    posUi.modalCard,
                    modalShellShared.modalCardShell,
                    modalExperience.modalWidthStandard,
                    { maxHeight: '92%', gap: spacing.sm },
                  ]}
                >
                  <ModalScreenHeader title="Elegí las fechas" subtitle="Desde y hasta." onClose={() => setCustomDatesOpen(false)} />
                  <View style={[modalExperience.formPanel, { flexDirection: 'row' }]}>
                    <View style={modalExperience.filterDateCol}>
                      <Text style={modalExperience.filterDateHint}>Desde</Text>
                      <InventoryIsoDateField
                        value={vm.draftFromYmd}
                        onChange={vm.setDraftFromYmd}
                        emptyLabel="Elegí la fecha inicial"
                        ariaLabel="Fecha desde"
                        showClearButton={false}
                        iosEmptyBehavior="openPicker"
                      />
                    </View>
                    <View style={modalExperience.filterDateCol}>
                      <Text style={modalExperience.filterDateHint}>Hasta</Text>
                      <InventoryIsoDateField
                        value={vm.draftToYmd}
                        onChange={vm.setDraftToYmd}
                        emptyLabel="Elegí la fecha final"
                        ariaLabel="Fecha hasta"
                        showClearButton={false}
                        iosEmptyBehavior="openPicker"
                      />
                    </View>
                  </View>
                  <View style={modalExperience.detailActionsPanel}>
                    <View style={modalExperience.detailActionsGrid}>
                      <TouchableOpacity
                        style={modalExperience.detailActionBtn}
                        onPress={() => setCustomDatesOpen(false)}
                        accessibilityRole="button"
                      >
                        <Text style={modalExperience.detailActionText}>Cancelar</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary]}
                        onPress={() => { vm.onApplyDateFilters(); setCustomDatesOpen(false); }}
                        accessibilityRole="button"
                      >
                        <Text style={modalExperience.detailActionTextPrimary}>Aplicar</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              </View>
            </Modal>
          ) : null}
        </>
      }
    />
  );
}

const styles = StyleSheet.create({
  shell: {
    ...ticketSurfaces.screen,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  shellCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  filterTriggerMobile: {
    flex: 1,
    marginBottom: 0,
  },
  // ── "Más" trigger (misma celda que Exportar CSV) ──
  moreTileDesk: {
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    flexDirection: 'row',
    gap: 4,
  },
  moreTileDeskText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  moreTileMobile: {
    width: 42,
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // ── Skeleton de carga (proto ventas-ux-v1 §5) ──
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 14,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  skeletonTextCol: {
    flex: 1,
    gap: 6,
  },
  skeletonBlock: {
    height: 13,
    borderRadius: 6,
    backgroundColor: ticketColors.ruleSoft,
  },
  skeletonBlockThin: {
    height: 10,
  },
});
